import { api, ApiError, revenueCycleId } from '@/lib/api';
import { denialReason, type Ability } from '@/lib/permissions';
import { useAuth } from '@/lib/use-auth';
import { InlineField, type InlineSaveResult } from '@/components/ui/InlineField';
import type { RevenueTrackerRow } from '@/lib/types';

/**
 * The ONE editable field on the Revenue Tracker.
 *
 * ⚠️ **EVERY OTHER CELL ON THIS PAGE IS DERIVED AND THIS APP NEVER WRITES IT.**
 * The company, its billing region, the services, the amounts and the totals are
 * all read through quote and invoice lines at request time — the same rule the
 * two sibling trackers run on, and for the same reason: a stored copy drifts
 * against the document the customer was actually sent, silently, with nothing
 * failing. Adding a control to any of them would either do nothing or store a
 * second answer to a question the line already answers.
 *
 * ⚠️ **THAT IS WHY THE PENCIL MATTERS.** `InlineField`'s glyph is visible at
 * rest (never `opacity-0` — see its own header, which records a feature reported
 * as missing because the affordance only appeared on hover), and it is the one
 * thing on a row of plain text saying *this cell, and only this cell, is
 * yours*. The page repeats the claim on the column heading so it is answerable
 * without hovering anything.
 */

/** Everything a notes cell needs from its writer, so no call site gates one differently. */
export interface RevenueNotesWriter {
  /** Whether the reader may write notes at all. */
  canEdit: boolean;
  /** Why not, in one sentence, when they may not. */
  disabledReason: string | undefined;
  /** Performs one write and reports in `InlineField`'s vocabulary. */
  save: (
    companyId: number,
    cycleKey: string,
    notes: string | null,
  ) => Promise<{ result: InlineSaveResult; stored: string | null }>;
}

/**
 * Gating and the write, once per page.
 *
 * 🔴 **GATED ON `canUpdate('companies')`, AND THAT IS A CHOICE RATHER THAN A
 * MEASUREMENT.** The server guard on `PUT /revenue-tracker/notes` is
 * `WRITE_ROLES` — a ROLE-GROUP guard with no permission module of its own, so
 * there is no `revenue-tracker` slug to ask about. `companies` is the closest
 * true statement available: it is `WRITE_ROLES` on the server too, and a row on
 * this tracker IS a company. The sibling Invoice Tracker gates its typed fields
 * on `canUpdate('invoices')` by the same reasoning, because its rows are
 * invoices.
 *
 * ⚠️ **`trackers-revenue` IS NOT THE ANSWER AND MUST NEVER BE USED HERE.** That
 * slug is a client-side NAV VIEW CONTROL that nothing on the server enforces,
 * and `enforcedByRole` refuses every action on it but `read` — a control behind
 * `canUpdate('trackers-revenue')` would be permanently disabled with no true
 * explanation to give.
 *
 * ⚠️ **The gate is the OPTIMISATION; the 403 branch below is the GUARANTEE.** A
 * role can change under a live access token, which lasts over five days on this
 * deployment.
 *
 * The control is mounted UNCONDITIONALLY and self-gates, so a read-only account
 * still sees every note with a sentence under it rather than an absence — a
 * hidden editor makes a tracker look like one with no notes in it.
 */
/**
 * ⚠️ THE CYCLE LIST IS BOUND HERE, ONCE, NOT THREADED THROUGH THE CELLS.
 *
 * The write needs a cycle ID and the grid is keyed by cycle NAME, so the
 * lookup has to happen somewhere. Passing the id list down to every notes
 * cell would put a second copy of the columns beside the `readonly string[]`
 * the rows already carry -- two lists describing one thing, agreeing only on
 * the day they were written. The writer is created once per page, so it is
 * the one place that can hold the list without duplicating it.
 */
export function useRevenueNotesWriter(
  cycles: readonly { id: number; name: string }[],
): RevenueNotesWriter {
  const { ability } = useAuth();
  const canEdit = ability.canUpdate('companies');
  const permissionReason = denialReason(ability, 'update', 'companies');

  const disabledReason = canEdit
    ? undefined
    : (permissionReason ?? 'Your account does not have permission to change this.');

  /*
   * A plain function, not a `useCallback`: it is only ever called from an
   * `InlineField`'s commit handler, which is a fresh closure every render
   * anyway, so memoising it would assert a stability nothing depends on.
   */
  const save = async (
    companyId: number,
    cycleKey: string,
    notes: string | null,
  ): Promise<{ result: InlineSaveResult; stored: string | null }> => {
    /*
     * 🔴 REFUSE RATHER THAN SEND A FABRICATED ID.
     *
     * `row.cycles` is keyed by the cycle NAME and the endpoint wants the
     * numeric id, so this lookup IS the write. When it misses -- a column
     * rendered from the row-key fallback against a server too old to publish
     * `meta.cycles` -- there is no id to send, and inventing one would file
     * the note against a DIFFERENT financial year: a write that succeeds,
     * reads back correct, and is wrong about the only thing it records.
     */
    const cycleId = revenueCycleId(cycleKey, cycles);
    if (cycleId === null) {
      return {
        result: {
          status: 'error',
          message: `This app cannot identify the ${cycleKey} column, so the note was not saved. Reload the page and try again.`,
        },
        stored: null,
      };
    }

    try {
      const response = await api.updateRevenueTrackerNotes({
        companyId,
        cycleId,
        notes,
      });

      /*
       * ⚠️ **RENDER FROM WHAT CAME BACK WHERE THERE IS ANYTHING TO READ, AND
       * SAY SO WHEN THERE IS NOT.** The server trims and normalises `''` to
       * `null`, so what was sent is not necessarily what is stored — but the
       * response shape for this endpoint was NOT published with the contract,
       * so this cannot assume a row comes back. A `200` proves acceptance and
       * never the shape of the body; this repo has paid for that three times on
       * Xero.
       *
       * So: adopt the server's value when the body carries one for THIS cycle,
       * and otherwise fall back to what we sent. The fallback is honest — the
       * only difference it can hide is a trim, which the local commit has
       * already applied.
       */
      const echoed = readEchoedNotes(response.data, cycleKey);
      return { result: { status: 'saved' }, stored: echoed === undefined ? notes : echoed };
    } catch (caught) {
      return { result: describeFailure(caught, ability), stored: null };
    }
  };

  return { canEdit, disabledReason, save };
}

/**
 * The stored note for one cycle, out of a response body we cannot rely on.
 *
 * Returns `undefined` for "the body said nothing about this", which is a third
 * answer and NOT the same as `null` — `null` is a real stored value meaning the
 * note was cleared. Collapsing the two would make an unreadable response clear
 * a note on screen that is still stored on the server.
 */
function readEchoedNotes(body: RevenueTrackerRow | undefined, cycleKey: string): string | null | undefined {
  const cell = body?.cycles?.[cycleKey];
  if (!cell) return undefined;
  return cell.notes ?? null;
}

/** One failure, in `InlineField`'s vocabulary. Split out so the writer reads as one path. */
function describeFailure(caught: unknown, ability: Ability): InlineSaveResult {
  if (caught instanceof ApiError) {
    /*
     * ⚠️ A 403 carries no `details` at all, so it can never be a field error —
     * it renders as prose beside the control instead.
     */
    if (caught.status === 403) {
      return {
        status: 'error',
        message: `${
          denialReason(ability, 'update', 'companies') ??
          'Your account no longer has permission to change this.'
        } Nothing was saved.`,
      };
    }
    /*
     * The (company, cycle) pair named in the body is what has gone — re-typing
     * cannot bring it back, and the row on screen is stale.
     */
    if (caught.status === 404) {
      return {
        status: 'error',
        message: 'This company and cycle are no longer on the tracker, so the note was not saved.',
      };
    }
    /*
     * Split against the ONE key this save sends. A 422 whose path names `notes`
     * belongs on this control; everything else — a `.strict()` rejection
     * (`path: ''` plus a `key`), or a path naming `companyId`/`cycleId`, which
     * have no control here — is unroutable.
     *
     * ⚠️ **A `cycleId` refusal is the one to expect first**, because the
     * label-or-id normalisation in `revenueCycleId` is a guess this app cannot
     * verify. It arrives here named, which is exactly what makes it
     * diagnosable rather than a mystery empty save.
     *
     * Reported as `warning` and not `error`: the request is over and the value
     * did not take, so marking the draft invalid would state something false
     * about it, and holding the editor open would invite a retype refused
     * identically.
     */
    const { fields, unroutable } = caught.routeFieldErrors(['notes']);
    const fieldText = Object.values(fields).join(' ');
    if (fieldText !== '') return { status: 'error', message: fieldText };
    if (unroutable.length > 0) {
      return { status: 'warning', message: `${unroutable.join(' ')} Nothing was saved.` };
    }
    return { status: 'error', message: caught.message };
  }
  if (caught instanceof Error && caught.message !== '') {
    return { status: 'error', message: caught.message };
  }
  return {
    status: 'error',
    message: 'Could not reach the server. Check your connection and try again.',
  };
}

/**
 * One (company, cycle) note, edited where it is read.
 *
 * ⚠️ **`display` is a TWO-LINE CLAMP with a `title`, not free wrapping.** A note
 * is the longest value on this table; an unbounded one sets the height of a
 * whole company block — the owner's *"ragged rows"* — while asking for width
 * that the cycle columns beside it then lose. The editor is unaffected: this is
 * the READ state, and opening it shows the whole note in a three-row textarea.
 *
 * ⚠️ `[overflow-wrap:anywhere]` STAYS. It is load-bearing beyond looks: it drops
 * the cell's min-content width to one character, which is what lets the
 * header-declared column widths hold. Remove it and one long unbroken token
 * widens the column past its declared width and squeezes every other one.
 *
 * ⚠️ **NO `maxLength`, DELIBERATELY.** The server's cap on this column was not
 * published with the contract, and an invented one refuses characters SILENTLY
 * — the textarea simply stops accepting them, with no message and nothing to
 * read. A server 422 says what the limit is; a guessed cap says nothing and can
 * be wrong in the direction that loses somebody's typing.
 */
export function RevenueNotesField({
  companyId,
  cycleKey,
  cycleLabel,
  notes,
  writer,
  onSaved,
}: {
  companyId: number;
  cycleKey: string;
  cycleLabel: string;
  notes: string | null;
  writer: RevenueNotesWriter;
  /** Called with the STORED value, so the cell renders the server's answer. */
  onSaved: (stored: string | null) => void;
}) {
  const value = notes ?? '';
  return (
    <InlineField
      /*
       * The label names the cycle as well as the column: this page renders one
       * of these per cycle per company, so a bare "Notes" would announce four
       * identical controls on one row ("Edit Notes: …") with nothing telling
       * them apart.
       */
      label={`Notes for ${cycleLabel}`}
      kind="multiline"
      rows={3}
      value={value}
      display={
        value.trim() === '' ? undefined : (
          <span title={value} className="line-clamp-2 text-xs text-fg-secondary [overflow-wrap:anywhere]">
            {value}
          </span>
        )
      }
      onSave={async (next) => {
        /*
         * `''` is a CLEAR and is sent as an explicit `null`, which is what this
         * API's text columns mean by it. `InlineField` has already trimmed.
         */
        const { result, stored } = await writer.save(companyId, cycleKey, next === '' ? null : next);
        if (result.status === 'saved') onSaved(stored);
        return result;
      }}
      disabled={!writer.canEdit}
      {...(writer.disabledReason === undefined ? {} : { disabledReason: writer.disabledReason })}
    />
  );
}
